import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { problemMessage } from '../../api/baseApi'
import {
  useAddAdjustmentMutation,
  useAddAgreementMutation,
  useGetTenantBillingQuery,
  useListPlansQuery,
  useRecordPaymentMutation,
  useSetPeriodNoteMutation,
  useSetTenantBilledMutation,
  useVoidAdjustmentMutation,
  useVoidPaymentMutation,
  type Billing,
  type StatementLine,
} from '../../api/operator'
import { formatDate, formatMoney, parseMoney, todayISO } from '../../model/money'
import { Loading, QueryError } from '../../ui/QueryState'
import { Balance, centsText, MoneyField } from './common'
import { shown } from '../../api/useIntent'

type Open = 'agreement' | 'payment' | 'adjustment' | null

/**
 * A tenant's account: what it agreed to, what that has charged, what
 * arrived, and what is owed.
 *
 * Every write answers with the account as it now stands, so the
 * statement redraws from the response rather than from a second read.
 */
export function TenantBilling({ tenant, active }: { tenant: string; active: boolean }) {
  const billing = useGetTenantBillingQuery({ tenant })
  const [open, setOpen] = useState<Open>(null)
  const [voiding, setVoiding] = useState<StatementLine | null>(null)
  const [noting, setNoting] = useState<StatementLine | null>(null)

  if (billing.isLoading) return <Loading label="Loading the account…" />
  if (billing.error || !billing.data) return <QueryError error={billing.error} title="Could not load the account" />
  const account = billing.data

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Figure label="Balance" value={<Balance cents={account.balance} />} />
        <Figure
          label="Overdue"
          value={account.overdue > 0 ? formatMoney(account.overdue) : '—'}
          caption={account.oldestOverdue && `since ${formatDate(account.oldestOverdue)}`}
          alert={account.overdue > 0}
        />
        <Figure
          label="Next charge"
          value={account.next ? formatMoney(account.next.amount) : '—'}
          caption={account.next && `on ${formatDate(account.next.periodStart)}, due ${formatDate(account.next.due)}`}
        />
      </Stack>

      <AgreementPanel tenant={tenant} account={account} active={active} onChange={() => setOpen('agreement')} />

      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={() => setOpen('payment')}>
          Record payment
        </Button>
        <Button onClick={() => setOpen('adjustment')}>Add charge or credit</Button>
      </Stack>

      <Statement account={account} onVoid={setVoiding} onNote={setNoting} />

      {/* Mounted only while open, so each starts afresh: the agreement as
          it stands now rather than as it stood when the screen first drew,
          and a form with neither the last attempt's values nor its refusal. */}
      {open === 'agreement' && <AgreementDialog open onClose={() => setOpen(null)} tenant={tenant} account={account} />}
      {open === 'payment' && <PaymentDialog onClose={() => setOpen(null)} tenant={tenant} methods={account.methods} />}
      {open === 'adjustment' && <AdjustmentDialog onClose={() => setOpen(null)} tenant={tenant} />}
      <VoidDialog tenant={tenant} line={voiding} onClose={() => setVoiding(null)} />
      <NoteDialog tenant={tenant} line={noting} onClose={() => setNoting(null)} />
    </Stack>
  )
}

function Figure({ label, value, caption, alert }: { label: string; value: React.ReactNode; caption?: string; alert?: boolean }) {
  return (
    <Paper sx={{ p: 2, flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
      <Typography variant="h2" sx={{ color: alert ? 'error.main' : 'text.primary' }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Paper>
  )
}

/**
 * The agreement in force, and whether the tenant is billed at all.
 *
 * A tenant that is not billed is offered no plan, because the platform
 * refuses one. Stopping billing ends an agreement that has not ended,
 * so it asks first only when there is one to end.
 */
function AgreementPanel({ tenant, account, active, onChange }: { tenant: string; account: Billing; active: boolean; onChange: () => void }) {
  const [setBilled, setting] = useSetTenantBilledMutation()
  const [stopping, setStopping] = useState(false)
  const current = account.current
  const latest = account.agreements[account.agreements.length - 1]
  const pending = latest && latest !== current && !latest.endsOn && (!current || latest.effectiveFrom > current.effectiveFrom) ? latest : undefined
  const running = Boolean(latest && !latest.endsOn)
  const cancel = () => {
    setStopping(false)
    setting.reset()
  }

  const mark = (billed: boolean) =>
    setBilled({ tenant, setBilled: { billed } })
      .unwrap()
      .then(() => setStopping(false))
      .catch(shown)

  return (
    <Paper sx={{ p: 2 }}>
      {Boolean(setting.error) && !stopping && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {problemMessage(setting.error, 'Billing was not changed.')}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h3">Agreement</Typography>
          {!account.billed ? (
            <Typography color="text.secondary">Not billed. Nothing is charged, and it is on no plan.</Typography>
          ) : current ? (
            <Typography>
              <strong>{current.planName}</strong>, {formatMoney(current.amount)} {current.interval === 'annual' ? 'a year' : 'a month'}, due{' '}
              {current.paymentTermsDays} days after each period starts. Since {formatDate(current.effectiveFrom)}
              {current.endsOn && <>, ending {formatDate(current.endsOn)}</>}.
            </Typography>
          ) : (
            <Typography color="text.secondary">{account.agreements.length ? 'No agreement is in force.' : 'Not on a plan yet.'}</Typography>
          )}
          {pending && (
            <Typography variant="body2" color="text.secondary">
              Changing to {pending.planName}, {formatMoney(pending.amount)} {pending.interval}, from {formatDate(pending.effectiveFrom)}.
            </Typography>
          )}
        </Box>
        {active && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignItems: 'flex-start' }}>
            {account.billed ? (
              <>
                <Button onClick={onChange}>{running ? 'Change agreement' : 'Put on a plan'}</Button>
                <Button color="inherit" disabled={setting.isLoading} onClick={() => (running ? setStopping(true) : void mark(false))}>
                  Stop billing
                </Button>
              </>
            ) : (
              <Button disabled={setting.isLoading} onClick={() => void mark(true)}>
                Bill this tenant
              </Button>
            )}
          </Stack>
        )}
      </Stack>
      <Dialog open={stopping} onClose={cancel} maxWidth="xs" fullWidth>
        <DialogTitle>Stop billing {tenant}?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              Its agreement ends today, along with any change scheduled after it. Periods that started before today stay owed,
              and payments can still be recorded against them.
            </DialogContentText>
            {Boolean(setting.error) && <Alert severity="error">{problemMessage(setting.error, 'Billing was not changed.')}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancel}>Cancel</Button>
          <Button variant="contained" disabled={setting.isLoading} onClick={() => void mark(false)}>
            Stop billing
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

const statusColours = { paid: 'success', 'part-paid': 'info', due: 'default', overdue: 'error' } as const

function describe(line: StatementLine): string {
  switch (line.kind) {
    case 'charge':
      return `${line.planName ?? 'Plan'}, ${formatDate(line.date)} – ${formatDate(line.periodEnd)}`
    case 'adjustment':
      return `${line.debit ? 'Charge' : 'Credit'}: ${line.reason ?? ''}`
    case 'payment':
      return `Payment${line.method ? ` by ${line.method.replace('-', ' ')}` : ''}${line.reference ? ` — ${line.reference}` : ''}`
  }
}

/**
 * The statement, oldest first, with the running balance. A void line
 * stays, struck through, because the ledger forgets nothing; it simply
 * stops counting.
 */
function Statement({
  account,
  onVoid,
  onNote,
}: {
  account: Billing
  onVoid: (line: StatementLine) => void
  onNote: (line: StatementLine) => void
}) {
  if (account.lines.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">Nothing has been charged or paid yet.</Typography>
      </Paper>
    )
  }
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">Owed</TableCell>
            <TableCell align="right">Paid</TableCell>
            <TableCell align="right">Balance</TableCell>
            <TableCell>Status</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {account.lines.map((line, index) => {
            const voided = Boolean(line.void)
            return (
              <TableRow key={line.id ?? `${line.kind}-${line.date}-${index}`} sx={voided ? { '& td': { color: 'text.disabled' } } : undefined}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(line.date)}</TableCell>
                <TableCell sx={voided ? { textDecoration: 'line-through' } : undefined}>
                  {describe(line)}
                  {(line.invoiceRef || line.note || line.void) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textDecoration: 'none' }}>
                      {[line.invoiceRef && `Invoice ${line.invoiceRef}`, line.note, line.void && `Void: ${line.void.reason}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{line.debit ? formatMoney(line.amount) : ''}</TableCell>
                <TableCell align="right">{line.debit ? '' : formatMoney(line.amount)}</TableCell>
                <TableCell align="right">{formatMoney(line.balance)}</TableCell>
                <TableCell>
                  {line.status && !voided && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={statusColours[line.status]}
                      label={line.status === 'part-paid' ? `${formatMoney(line.outstanding ?? 0)} to pay` : line.status}
                    />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {line.kind === 'charge' && (
                    <Button size="small" onClick={() => onNote(line)}>
                      {line.invoiceRef ? 'Edit invoice' : 'Invoice #'}
                    </Button>
                  )}
                  {line.kind !== 'charge' && !voided && (
                    <Button size="small" color="inherit" onClick={() => onVoid(line)}>
                      Void
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function AgreementDialog({ open, onClose, tenant, account }: { open: boolean; onClose: () => void; tenant: string; account: Billing }) {
  const plans = useListPlansQuery(undefined, { skip: !open })
  const [add, adding] = useAddAgreementMutation()
  const current = account.current
  const latest = account.agreements[account.agreements.length - 1]
  const running = Boolean(latest && !latest.endsOn)
  const [planId, setPlanId] = useState(current?.planId ?? '')
  const [interval, setInterval] = useState<'monthly' | 'annual'>(current?.interval ?? 'monthly')
  const [amount, setAmount] = useState(centsText(current?.amount))
  const [from, setFrom] = useState(account.nextBoundary ?? todayISO())
  const [terms, setTerms] = useState(String(current?.paymentTermsDays ?? 20))

  const offered = (plans.data?.plans ?? []).filter((plan) => plan.active || plan.id === planId)
  const reprice = (nextPlan: string, nextInterval: 'monthly' | 'annual') => {
    const plan = offered.find((candidate) => candidate.id === nextPlan)
    if (plan) setAmount(centsText(nextInterval === 'annual' ? plan.annual : plan.monthly))
  }
  const cents = parseMoney(amount)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{running ? 'Change agreement' : 'Put on a plan'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {Boolean(adding.error) && <Alert severity="error">{problemMessage(adding.error, 'The agreement was not changed.')}</Alert>}
          {account.nextBoundary && (
            <DialogContentText>
              A change takes effect at the start of a period; the next one is {formatDate(account.nextBoundary)}. There is no
              proration — credit a partly used period with an adjustment.
            </DialogContentText>
          )}
          <TextField
            select
            label="Plan"
            value={planId}
            onChange={(event) => {
              setPlanId(event.target.value)
              reprice(event.target.value, interval)
            }}
          >
            {offered.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.name}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Pays"
              value={interval}
              onChange={(event) => {
                const next = event.target.value as 'monthly' | 'annual'
                setInterval(next)
                reprice(planId, next)
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="annual">Annually</MenuItem>
            </TextField>
            <MoneyField label="Amount per period" value={amount} onChange={setAmount} fullWidth />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Takes effect"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
            <TextField label="Terms (days)" value={terms} onChange={(event) => setTerms(event.target.value)} fullWidth />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!planId || cents === null || !from || adding.isLoading}
          onClick={() =>
            add({
              tenant,
              addAgreement: { planId, interval, amount: cents ?? undefined, effectiveFrom: from, paymentTermsDays: Number(terms) || 0 },
            })
              .unwrap()
              .then(onClose)
              .catch(shown)
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function PaymentDialog({ onClose, tenant, methods }: { onClose: () => void; tenant: string; methods: string[] }) {
  const [record, recording] = useRecordPaymentMutation()
  const [amount, setAmount] = useState('')
  const [receivedOn, setReceivedOn] = useState(todayISO())
  const [method, setMethod] = useState(methods[0] ?? 'bank-transfer')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const cents = parseMoney(amount)

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Record payment</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {Boolean(recording.error) && <Alert severity="error">{problemMessage(recording.error, 'The payment was not recorded.')}</Alert>}
          <MoneyField label="Amount" value={amount} onChange={setAmount} autoFocus />
          <TextField
            label="Received"
            type="date"
            value={receivedOn}
            onChange={(event) => setReceivedOn(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField select label="Method" value={method} onChange={(event) => setMethod(event.target.value)}>
            {methods.map((option) => (
              <MenuItem key={option} value={option}>
                {option.replace('-', ' ')}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Reference" value={reference} onChange={(event) => setReference(event.target.value)} helperText="As it appears on the bank statement." />
          <TextField label="Note" value={note} onChange={(event) => setNote(event.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!cents || !receivedOn || recording.isLoading}
          onClick={() =>
            record({ tenant, recordPayment: { amount: cents ?? 0, receivedOn, method, reference: reference || undefined, note: note || undefined } })
              .unwrap()
              .then(onClose)
              .catch(shown)
          }
        >
          Record
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function AdjustmentDialog({ onClose, tenant }: { onClose: () => void; tenant: string }) {
  const [add, adding] = useAddAdjustmentMutation()
  const [kind, setKind] = useState<'charge' | 'credit'>('credit')
  const [amount, setAmount] = useState('')
  const [on, setOn] = useState(todayISO())
  const [reason, setReason] = useState('')
  const cents = parseMoney(amount)

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add a charge or credit</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {Boolean(adding.error) && <Alert severity="error">{problemMessage(adding.error, 'The adjustment was not made.')}</Alert>}
          <TextField select label="Kind" value={kind} onChange={(event) => setKind(event.target.value as 'charge' | 'credit')}>
            <MenuItem value="credit">Credit — a discount, goodwill or write-off</MenuItem>
            <MenuItem value="charge">Charge — a one-off fee</MenuItem>
          </TextField>
          <MoneyField label="Amount" value={amount} onChange={setAmount} />
          <TextField label="Date" type="date" value={on} onChange={(event) => setOn(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} required />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!cents || !on || !reason.trim() || adding.isLoading}
          onClick={() =>
            add({ tenant, addAdjustment: { kind, amount: cents ?? 0, on, reason: reason.trim() } })
              .unwrap()
              .then(onClose)
              .catch(shown)
          }
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// A form per line, so a reason typed for one line and cancelled is never
// offered as the reason for voiding the next.
function VoidDialog({ tenant, line, onClose }: { tenant: string; line: StatementLine | null; onClose: () => void }) {
  return line ? <VoidForm key={line.id} tenant={tenant} line={line} onClose={onClose} /> : null
}

function VoidForm({ tenant, line, onClose }: { tenant: string; line: StatementLine; onClose: () => void }) {
  const [voidPayment, payment] = useVoidPaymentMutation()
  const [voidAdjustment, adjustment] = useVoidAdjustmentMutation()
  const [reason, setReason] = useState('')
  const error = payment.error ?? adjustment.error
  const busy = payment.isLoading || adjustment.isLoading

  const submit = async () => {
    if (!line.id) return
    const body = { tenant, entry: line.id, voidEntry: { reason: reason.trim() } }
    await (line.kind === 'payment' ? voidPayment(body) : voidAdjustment(body)).unwrap()
    onClose()
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Void this {line.kind}?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DialogContentText>
            {`${describe(line)}, ${formatMoney(line.amount)} on ${formatDate(line.date)}.`} It stays on the statement, struck
            through, and stops counting.
          </DialogContentText>
          {Boolean(error) && <Alert severity="error">{problemMessage(error, 'It was not voided.')}</Alert>}
          <TextField label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} autoFocus required />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" variant="contained" disabled={!reason.trim() || busy} onClick={() => submit().catch(shown)}>
          Void
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function NoteDialog({ tenant, line, onClose }: { tenant: string; line: StatementLine | null; onClose: () => void }) {
  return line ? <NoteForm key={line.date} tenant={tenant} line={line} onClose={onClose} /> : null
}

function NoteForm({ tenant, line, onClose }: { tenant: string; line: StatementLine; onClose: () => void }) {
  const [save, saving] = useSetPeriodNoteMutation()
  const [invoiceRef, setInvoiceRef] = useState(line.invoiceRef ?? '')
  const [text, setText] = useState(line.note ?? '')
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{describe(line)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {Boolean(saving.error) && <Alert severity="error">{problemMessage(saving.error, 'It was not saved.')}</Alert>}
          <TextField label="Invoice number" value={invoiceRef} onChange={(event) => setInvoiceRef(event.target.value)} autoFocus />
          <TextField label="Note" value={text} onChange={(event) => setText(event.target.value)} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving.isLoading}
          onClick={() =>
            save({ tenant, periodStart: line.date, periodNote: { invoiceRef, text } })
              .unwrap()
              .then(onClose)
              .catch(shown)
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
